// «Что нового»: история версий, вшитая в сборку, разбор списка изменений и память показа.
//
// ## Зачем
//
// Финансовый директор 14.10.2026: «Пока не было обновлений». Программа при этом обновилась, но
// он этого не увидел: манифест называл одну общую фразу на все выпуски, а программа не
// показывала даже её. Просил он три вещи — значок с новым перед обновлением, список изменений
// после установки и историю версий в настройках, там же, где номер программы.
//
// ## Откуда берётся текст
//
// Источник один — `deploy/что-нового.json` (схема и правила — в `deploy/что_нового.py`). Сюда он
// приезжает копией `./что-нового.json`: браузерная форма собирается внутри каталога `web` и
// соседних каталогов не видит, путь через `../deploy` сломал бы её сборку на сервере. Копию
// кладёт выпуск, расхождение с источником ловит `что-нового.test.ts`.
//
// | что на экране | откуда текст |
// |---|---|
// | история установленных версий и «что нового» после установки | эта копия, без сети |
// | новая версия на компьютере, до установки | `notes` манифеста, их отдаёт апдейтер вместе с файлом |
// | новая версия на планшете, до установки | `releaseNotes` из ответа App Store |
//
// Текст НОВОЙ версии в сборку попасть не может в принципе: его пишут после неё. Поэтому до
// установки он приходит по сети, а после — уже лежит внутри.

import { сравнитьВерсии } from './обновление';
import источник from './что-нового.json';

/** Номер этой сборки. Тот же, что в манифесте обновлений: оба ставит `deploy/выпуск.sh`. */
export const ВЕРСИЯ_ПРОГРАММЫ: string = __ВЕРСИЯ_ПРОГРАММЫ__;

export interface ЗаписьВерсии {
  версия: string;
  /** День, когда версия дошла до людей, «ГГГГ-ММ-ДД». Пусто — черновик, к людям не уходил. */
  дата: string | null;
  новое: readonly string[];
  исправлено: readonly string[];
}

/** Группа пунктов. Заголовка нет у текста без разметки (так писали манифесты до 1.0.24). */
export interface ГруппаИзменений {
  заголовок: string | null;
  пункты: readonly string[];
}

/** Линии истории: кабинет (Windows и планшет — одна версия), айфон, Android. */
export type Линия = 'программа' | 'iphone' | 'android';

const ЗАГОЛОВОК_НОВОГО = 'Новое';
const ЗАГОЛОВОК_ИСПРАВЛЕННОГО = 'Исправлено';

function списокСтрок(значение: unknown): string[] {
  return Array.isArray(значение)
    ? значение.filter((пункт): пункт is string => typeof пункт === 'string' && пункт.trim() !== '')
    : [];
}

/**
 * Записи линии, от новой к старой. Читается недоверчиво: битая запись пропускается, а не
 * роняет окно программы. Годность самого файла держат проверки и выпуск, а не экран.
 */
export function записиЛинии(данные: unknown, линия: Линия): ЗаписьВерсии[] {
  const линии = (данные as { линии?: unknown } | null)?.линии;
  const сырые = (линии as Record<string, unknown> | undefined)?.[линия];
  if (!Array.isArray(сырые)) {
    return [];
  }
  const записи: ЗаписьВерсии[] = [];
  for (const сырая of сырые as unknown[]) {
    const запись = сырая as Record<string, unknown> | null;
    const версия = запись?.версия;
    if (typeof версия !== 'string' || !/^\d+\.\d+\.\d+$/.test(версия)) {
      continue;
    }
    записи.push({
      версия,
      дата: typeof запись?.дата === 'string' ? запись.дата : null,
      новое: списокСтрок(запись?.новое),
      исправлено: списокСтрок(запись?.исправлено),
    });
  }
  return записи.sort((а, б) => сравнитьВерсии(б.версия, а.версия));
}

const ЗАПИСИ_ПРОГРАММЫ = записиЛинии(источник, 'программа');

/**
 * История для человека: всё, что вышло, до установленной версии включительно.
 *
 * Черновик (запись без даты) не показывается никогда: это список, который ещё пишут. Версии
 * новее установленной тоже: о них человек узнает значком «Есть обновление», а не из истории
 * того, что у него уже стоит.
 */
export function историяДо(
  установлена: string = ВЕРСИЯ_ПРОГРАММЫ,
  записи: readonly ЗаписьВерсии[] = ЗАПИСИ_ПРОГРАММЫ,
): ЗаписьВерсии[] {
  return записи.filter(
    (запись) => запись.дата !== null && сравнитьВерсии(запись.версия, установлена) <= 0,
  );
}

/** Записи, вышедшие после `после` и до `до` включительно. Без нижней границы — одна `до`. */
export function записиМежду(
  после: string | null,
  до: string,
  записи: readonly ЗаписьВерсии[] = ЗАПИСИ_ПРОГРАММЫ,
): ЗаписьВерсии[] {
  return историяДо(до, записи).filter((запись) =>
    после === null
      ? сравнитьВерсии(запись.версия, до) === 0
      : сравнитьВерсии(запись.версия, после) > 0,
  );
}

/** Группы записи в том порядке, в каком их читают: сначала новое, потом исправленное. */
export function группыЗаписи(запись: ЗаписьВерсии): ГруппаИзменений[] {
  const группы: ГруппаИзменений[] = [];
  if (запись.новое.length > 0) {
    группы.push({ заголовок: ЗАГОЛОВОК_НОВОГО, пункты: запись.новое });
  }
  if (запись.исправлено.length > 0) {
    группы.push({ заголовок: ЗАГОЛОВОК_ИСПРАВЛЕННОГО, пункты: запись.исправлено });
  }
  return группы;
}

/**
 * Список изменений из текста манифеста или магазина.
 *
 * Формат собирает `deploy/что_нового.py`: заголовок группы отдельной строкой («Новое»,
 * «Исправлено», двоеточие допустимо), пункт начинается с «• ». Текст без разметки — так писали
 * до 1.0.24 («Правки и исправления системы «Барк».») — читается как одна группа без заголовка:
 * назвать его «новым» значило бы соврать, а выбросить — оставить человека с пустым окном.
 */
export function разобратьЗаметки(текст: string | null | undefined): ГруппаИзменений[] {
  if (текст === null || текст === undefined) {
    return [];
  }
  const группы: { заголовок: string | null; пункты: string[] }[] = [];
  let текущая: { заголовок: string | null; пункты: string[] } | null = null;
  for (const сырая of текст.split(/\r?\n/)) {
    const строка = сырая.trim();
    if (строка === '') {
      continue;
    }
    const заголовок = строка.replace(/:$/, '');
    if (заголовок === ЗАГОЛОВОК_НОВОГО || заголовок === ЗАГОЛОВОК_ИСПРАВЛЕННОГО) {
      текущая = { заголовок, пункты: [] };
      группы.push(текущая);
      continue;
    }
    if (текущая === null) {
      текущая = { заголовок: null, пункты: [] };
      группы.push(текущая);
    }
    const пункт = строка.replace(/^[•·\-–—*]\s*/, '');
    if (пункт !== '') {
      текущая.пункты.push(пункт);
    }
  }
  return группы.filter((группа) => группа.пункты.length > 0);
}

// ── Память: о какой версии уже рассказали ────────────────────────────────────────────────────
//
// Список после установки показывается ОДИН раз на версию и засчитывается фактом показа, как
// подсказки (`shared/hints/память.ts`): закрыть окно, не читая, человек вправе, и второй раз
// тот же список через день — это шум, а не забота.
//
// Память живёт на устройстве, а не у человека: установка — событие программы на этом
// компьютере. Хранится установленная версия, та, что стояла до неё, и рассказано ли.

const КЛЮЧ = 'bark.whats-new';

/** Разрешение показывать список в автоматическом браузере. Ставит только снимающий сценарий. */
const РАЗРЕШЕНО_В_ПРОГОНЕ = 'bark.whats-new.show-in-automation';

interface Память {
  /** Версия, с которой программа запускалась в последний раз. */
  версия: string;
  /** Версия до неё: с какой рассказывать. Пусто — неизвестно, тогда только о `версия`. */
  прежняя: string | null;
  /** Рассказали ли о `версия`. */
  рассказано: boolean;
}

function хранилище(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function прочитать(место: Storage): Память | null {
  try {
    const сырое = место.getItem(КЛЮЧ);
    if (сырое === null) {
      return null;
    }
    const данные = JSON.parse(сырое) as Partial<Память> | null;
    if (typeof данные?.версия !== 'string') {
      return null;
    }
    return {
      версия: данные.версия,
      прежняя: typeof данные.прежняя === 'string' ? данные.прежняя : null,
      рассказано: данные.рассказано === true,
    };
  } catch {
    return null;
  }
}

function записать(место: Storage, память: Память): void {
  try {
    место.setItem(КЛЮЧ, JSON.stringify(память));
  } catch {
    // Не записали — список покажется ещё раз при следующем запуске. Работу это не ломает.
  }
}

/**
 * Работала ли здесь программа до этого запуска.
 *
 * Нужно ровно для одного случая: первой версии с этой памятью. У того, кто обновился с 1.0.23,
 * записи о рассказанном ещё нет — как и у того, кто поставил программу впервые. Первому
 * рассказать надо, второму не о чем: для него всё новое. Различает их след прежней работы —
 * любая запись программы на этом устройстве (вход, тема, кэш экранов).
 */
function былиСледы(место: Storage): boolean {
  for (let i = 0; i < место.length; i += 1) {
    const ключ = место.key(i);
    if (ключ !== null && ключ.startsWith('bark.') && !ключ.startsWith(КЛЮЧ)) {
      return true;
    }
  }
  return false;
}

/**
 * Отметить запуск этой версии. Зовётся ОДИН раз, до первого кадра (`app/App.tsx`): позже
 * программа сама пишет в хранилище, и первый запуск станет неотличим от обновления.
 *
 * | было | стало |
 * |---|---|
 * | памяти нет, следов нет | первая установка: рассказывать не о чем |
 * | памяти нет, следы есть | обновление с версии без этой памяти: рассказать об этой |
 * | версия выросла | обновление: рассказать обо всём, что вышло после рассказанного |
 * | та же версия | ничего |
 * | версия стала меньше | откат: о старом не рассказываем |
 */
export function отметитьЗапуск(
  версия: string = ВЕРСИЯ_ПРОГРАММЫ,
  место: Storage | null = хранилище(),
  записи: readonly ЗаписьВерсии[] = ЗАПИСИ_ПРОГРАММЫ,
): void {
  if (место === null) {
    return;
  }
  const память = прочитать(место);
  if (память === null) {
    if (!былиСледы(место)) {
      записать(место, { версия, прежняя: null, рассказано: true });
      return;
    }
    // С какой версии пришёл человек, неизвестно: рассказываем об этой одной.
    записать(место, { версия, прежняя: null, рассказано: false });
    return;
  }
  const разница = сравнитьВерсии(версия, память.версия);
  if (разница === 0) {
    return;
  }
  if (разница < 0) {
    записать(место, { версия, прежняя: null, рассказано: true });
    return;
  }
  // Не рассказанное в прошлый раз не теряется: обновился дважды, ни разу не войдя, — список
  // начинается с того, что он видел последним, а не с промежуточной версии.
  const прежняя = память.рассказано ? память.версия : память.прежняя;
  записать(место, {
    версия,
    прежняя,
    рассказано: записиМежду(прежняя, версия, записи).length === 0,
  });
}

/** Что рассказать после установки: версии (`после`, `до`]. Пусто — рассказывать нечего. */
export function ждётРассказа(
  место: Storage | null = хранилище(),
  записи: readonly ЗаписьВерсии[] = ЗАПИСИ_ПРОГРАММЫ,
): { после: string | null; до: string } | null {
  const память = место === null ? null : прочитать(место);
  if (память === null || память.рассказано) {
    return null;
  }
  if (записиМежду(память.прежняя, память.версия, записи).length === 0) {
    return null;
  }
  return { после: память.прежняя, до: память.версия };
}

/**
 * Записи для окна «Что нового в X». Окно открыто сразу после установки — рассказывает обо всём
 * пропущенном; открыто ссылкой позже — об одной версии X.
 */
export function чтоНовогоВ(
  версия: string,
  место: Storage | null = хранилище(),
  записи: readonly ЗаписьВерсии[] = ЗАПИСИ_ПРОГРАММЫ,
): ЗаписьВерсии[] {
  const память = место === null ? null : прочитать(место);
  const после = память !== null && память.версия === версия ? память.прежняя : null;
  const найдено = записиМежду(после, версия, записи);
  return найдено.length > 0 ? найдено : записиМежду(null, версия, записи);
}

/** Рассказали: окно «Что нового» нарисовано. Второй раз само оно не откроется. */
export function отметитьРассказ(версия: string, место: Storage | null = хранилище()): void {
  if (место === null) {
    return;
  }
  const память = прочитать(место);
  if (память === null || память.версия !== версия || память.рассказано) {
    return;
  }
  записать(место, { ...память, рассказано: true });
}

/**
 * Открывать ли список самому в автоматическом браузере. По умолчанию — нет, по той же причине,
 * что у подсказок: окно поверх экрана в каждой из тысячи сквозных проверок накрывало бы то,
 * что они проверяют. Посмотреть глазами можно явным разрешением.
 */
export function показРазрешён(место: Storage | null = хранилище()): boolean {
  const автомат = typeof navigator !== 'undefined' && navigator.webdriver;
  if (!автомат) {
    return true;
  }
  try {
    return место?.getItem(РАЗРЕШЕНО_В_ПРОГОНЕ) === 'да';
  } catch {
    return false;
  }
}

/** Только для тестов: забыть, о чём рассказали. */
export function забытьРассказДляТестов(место: Storage | null = хранилище()): void {
  try {
    место?.removeItem(КЛЮЧ);
  } catch {
    // Забывать нечего.
  }
}
